import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Percent, Zap } from 'lucide-react';
interface ROIResult {
  extraDeals: number;
  newTotalDeals: number;
  extraProfit: number;
  newTotalProfit: number;
  roiPercentage: number;
  breakEvenDeals: number;
  planCost: number;
}
interface ROIResultCardProps {
  results: ROIResult;
}
/**
 * ROIResultCard component
 * Displays AcquireFlow-specific ROI results with animated counters
 */
export function ROIResultCard({
  results
}: ROIResultCardProps) {
  const [animatedResults, setAnimatedResults] = useState<ROIResult>({
    extraDeals: 0,
    newTotalDeals: 0,
    extraProfit: 0,
    newTotalProfit: 0,
    roiPercentage: 0,
    breakEvenDeals: 0,
    planCost: 0
  });
  // Animate numbers when results change
  useEffect(() => {
    const duration = 1000;
    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      setAnimatedResults({
        extraDeals: results.extraDeals * progress,
        newTotalDeals: results.newTotalDeals * progress,
        extraProfit: Math.floor(results.extraProfit * progress),
        newTotalProfit: Math.floor(results.newTotalProfit * progress),
        roiPercentage: Math.floor(results.roiPercentage * progress),
        breakEvenDeals: results.breakEvenDeals * progress,
        planCost: results.planCost
      });
      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedResults(results);
      }
    }, stepDuration);
    return () => clearInterval(interval);
  }, [results]);
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };
  const formatDealsRange = (deals: number) => {
    const lower = Math.floor(deals);
    const upper = Math.ceil(deals);
    if (lower === upper) return `${lower}`;
    return `${lower}-${upper}`;
  };
  return <div className="h-full w-full relative bg-[#18181b] border-2 border-white/20 rounded-2xl p-6 md:p-8 overflow-hidden flex flex-col shadow-[0_0_60px_20px_rgba(58,183,149,0.08),0_0_30px_10px_rgba(255,255,255,0.12)]" data-id="element-2098">
      {/* Background Effects - White with teal hints */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" data-id="element-2099" />
      <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#3ab795]/10 to-white/10 rounded-full blur-3xl" data-id="element-2100" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-white/10 to-[#3ab795]/10 rounded-full blur-3xl" data-id="element-2101" />

      <div className="relative z-10 space-y-6 flex-1 flex flex-col" data-id="element-2102">
        {/* Header */}
        <div className="text-center space-y-3 pb-4 border-b border-white/10" data-id="element-2103">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full" data-id="element-2104">
            <Zap className="w-4 h-4 text-[#3ab795]" data-id="element-2105" />
            <span className="text-xs font-medium text-[#3ab795] uppercase tracking-wider" data-id="element-2106">
              Your New Reality
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-2107">
            with AcquireFlow
          </h3>
        </div>

        {/* Main ROI Display */}
        <div className="text-center py-4" data-id="element-2108">
          <div className="text-4xl md:text-5xl font-bold text-white mb-1" data-id="element-2109">
            {formatCurrency(animatedResults.extraProfit)}
            <span className="text-xl md:text-2xl text-[#b8bcc8] font-normal" data-id="element-2110">
              /month
            </span>
          </div>
          <p className="text-xs text-[#b8bcc8]" data-id="element-2111">
            Extra profit with AcquireFlow
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 gap-3 flex-1" data-id="element-2112">
          {/* Extra Deals */}
          <div className="bg-[#27272a] border border-white/10 rounded-xl p-4" data-id="element-2113">
            <div className="flex items-center gap-3" data-id="element-2114">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center" data-id="element-2115">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-2116" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2117">
                <div className="text-2xl md:text-3xl font-bold text-white" data-id="element-2118">
                  +{formatDealsRange(animatedResults.extraDeals)}
                </div>
                <div className="text-xs text-[#b8bcc8] mt-0.5" data-id="element-2119">
                  Extra deals per month
                </div>
              </div>
            </div>
          </div>

          {/* ROI Percentage */}
          <div className="bg-[#27272a] border border-white/10 rounded-xl p-4" data-id="element-2120">
            <div className="flex items-center gap-3" data-id="element-2121">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center" data-id="element-2122">
                <Percent className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-2123" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2124">
                <div className="text-2xl md:text-3xl font-bold text-white" data-id="element-2125">
                  {animatedResults.roiPercentage.toLocaleString()}%
                </div>
                <div className="text-xs text-[#b8bcc8] mt-0.5" data-id="element-2126">
                  Return on investment
                </div>
              </div>
            </div>
          </div>

          {/* Break-Even */}
          <div className="bg-[#27272a] border border-white/10 rounded-xl p-4" data-id="element-2127">
            <div className="flex items-center gap-3" data-id="element-2128">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center" data-id="element-2129">
                <Zap className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-2130" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2131">
                <div className="text-2xl md:text-3xl font-bold text-white" data-id="element-2132">
                  {animatedResults.breakEvenDeals.toFixed(2)}
                </div>
                <div className="text-xs text-[#b8bcc8] mt-0.5" data-id="element-2133">
                  Deals to break even
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center pt-4" data-id="element-2134">
          <a href="/trial" className="group inline-flex items-center gap-2.5 px-6 py-3 bg-[#3ab795] text-white font-semibold text-base rounded-xl transition-all duration-300 hover:bg-[#2d9f7e] hover:scale-105 hover:shadow-[0_8px_24px_rgba(58,183,149,0.4)] active:scale-100" data-id="element-2135">
            <span data-id="element-2136">Join Beta</span>
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2137">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-2138" />
            </svg>
          </a>
        </div>

        {/* Social Proof */}
        <p className="text-center text-xs text-[#b8bcc8] leading-relaxed" data-id="element-2139">
          Based on 200+ active investors. Calculations use 0.25% industry
          conversion rate.
        </p>
      </div>
    </div>;
}